import { useLocation, useNavigate } from "react-router-dom";
import { Check, Crown, Calendar, Zap, Star, Users, Target, ArrowRight, Download } from "lucide-react";
import Button from "../../components/common/Button";

const SubscriptionSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state || {};
  const plan = state.plan;
  const subscription = state.subscription?.subscription || state.subscription || null;
  const transactionId = state.transactionId || state.reference;
  const instructions = state.instructions;

  const activePlan = subscription?.plan || plan;
  const endDate = subscription?.end_date ? new Date(subscription.end_date) : null;

  if (!activePlan) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md">
          <Crown className="w-12 h-12 text-primary-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">No Active Checkout Session</h2>
          <p className="text-gray-600 mb-6">Please select a plan to activate your subscription.</p>
          <Button onClick={() => navigate('/pricing')} className="w-full">
            View Pricing Plans
          </Button>
        </div>
      </div>
    );
  }

  // Feature icons mapping
  const featureIcons = {
    cv_processing: Zap,
    profile_boost: Star,
    job_applications: Target,
    priority_support: Users,
    basic_support: Users,
  };

  // Get active features
  const activeFeatures = Object.entries(activePlan.features || {})
    .filter(([_, value]) => value !== false && value !== null && value !== undefined)
    .map(([key, value]) => {
      const IconComponent = featureIcons[key] || Check;
      let label = key.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');

      if (typeof value === 'number') {
        if (key.includes('application') || key.includes('job')) {
          label = `${value} Job Applications`;
        } else {
          label = `${value} ${label}`;
        }
      }

      return { key, label, icon: IconComponent, value };
    });

  const handleExploreFeatures = () => {
    // Navigate to dashboard or features page
    navigate('/dashboard');
  };

  const handleDownloadReceipt = () => {
    // Generate and download receipt
    console.log('Download receipt for transaction:', transactionId);
    // You can implement receipt download logic here
  };

  const isPending = subscription?.status === 'pending';

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-primary-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`w-24 h-24 ${isPending ? 'bg-amber-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
            {isPending ? <Crown className="w-12 h-12 text-amber-600" /> : <Check className="w-12 h-12 text-green-600" />}
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {isPending ? `Payment Initiated for ${activePlan.name}` : `Welcome to ${activePlan.name}!`}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {isPending
              ? "Please follow the instructions below to complete your payment. Your subscription will be activated upon verification."
              : "Your subscription has been activated successfully. Start exploring your new features."}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Plan Summary Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Crown className="w-6 h-6 text-primary-600" />
                <h2 className="text-2xl font-bold text-gray-900">Your Active Plan</h2>
              </div>

              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{activePlan.name}</h3>
                  <p className="text-gray-600">{activePlan.description}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary-600">Ksh{activePlan.price}</p>
                  <p className="text-gray-500 text-sm">per month</p>
                </div>
              </div>

              {/* Subscription Period */}
              {endDate && (
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 mb-6">
                  <Calendar className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Subscription Period</p>
                    <p className="text-gray-600 text-sm">
                      Active until {endDate.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              )}

              {/* Transaction ID & Instructions */}
              {transactionId && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-500">
                    Reference ID: <span className="font-mono font-medium text-gray-800">{transactionId}</span>
                  </p>
                  {instructions && (
                    <p className="text-sm text-primary-700 bg-primary-50 p-3 rounded-lg mt-2">
                      {instructions}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Features Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Your Premium Features
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                {activeFeatures.map((feature) => (
                  <div key={feature.key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="p-2 bg-primary-100 text-primary-600 rounded-full">
                      <feature.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">
                        {feature.label}
                      </p>
                    </div>
                    {typeof feature.value === 'number' && (
                      <span className="bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded-full font-medium">
                        {feature.value}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - Quick Actions */}
          <div className="space-y-6">
            {/* Next Steps Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Next Steps
              </h3>

              <div className="space-y-3">
                <button
                  onClick={handleExploreFeatures}
                  className="w-full flex items-center justify-between p-3 text-left bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-primary-900">Explore Features</p>
                    <p className="text-primary-600 text-sm">Discover your new tools</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-primary-600" />
                </button>

                <button
                  onClick={() => navigate('/jobs')}
                  className="w-full flex items-center justify-between p-3 text-left bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-green-900">Browse Jobs</p>
                    <p className="text-green-600 text-sm">Find your next opportunity</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-green-600" />
                </button>

                {/* <button
                  onClick={handleDownloadReceipt}
                  className="w-full flex items-center justify-between p-3 text-left bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">Download Receipt</p>
                    <p className="text-gray-600 text-sm">Get payment confirmation</p>
                  </div>
                  <Download className="w-4 h-4 text-gray-600" />
                </button> */}
              </div>
            </div>

            {/* Support Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Need Help?
              </h3>
              <p className="text-blue-700 text-sm mb-4">
                Our support team is here to help you get the most out of your subscription.
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate('/support')}
                className="w-full"
              >
                Contact Support
              </Button>
            </div>

            {/* Upgrade Info */}
            {activePlan.name.includes('Basic') && (
              <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-purple-900 mb-2">
                  Want More?
                </h3>
                <p className="text-purple-700 text-sm mb-4">
                  Upgrade to Premium for even more features and benefits.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  View Premium Plans
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12">
          <Button
            onClick={handleExploreFeatures}
            size="lg"
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg"
            icon={<ArrowRight className="w-5 h-5" />}
            iconPosition="right"
          >
            Start Exploring Your Features
          </Button>

          <p className="text-gray-500 text-sm mt-4">
            You can manage your subscription anytime from your dashboard
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;